import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronLeft, ChevronRight, FastForward, Info } from 'lucide-react';
import { Screen1Hero } from './teaser/Screen1Hero';
import { Screen2Slider } from './teaser/Screen2Slider';
import { Screen3Ranking } from './teaser/Screen3Ranking';
import { Screen4Reveal } from './teaser/Screen4Reveal';
import { Screen5Memory } from './teaser/Screen5Memory';
import { Screen6Video } from './teaser/Screen6Video';
import { Screen7Input } from './teaser/Screen7Input';
interface Props {
  open: boolean;
  onClose: () => void;
}
export const Teaser = ({
  open,
  onClose
}: Props) => {
  const [screen, setScreen] = useState(0);
  const [showInfo, setShowInfo] = useState(false);
  useEffect(() => {
    if (!open) {
      // Reset shortly after close so the next open starts fresh
      const t = setTimeout(() => setScreen(0), 400);
      return () => clearTimeout(t);
    }
  }, [open]);
  const next = () => setScreen(s => Math.min(s + 1, 6));
  const prev = () => setScreen(s => Math.max(s - 1, 0));
  const restart = () => setScreen(0);
  const skipToEnd = () => setScreen(6);
  const screens = [<Screen1Hero key="1" onNext={next} data-id="element-251" />, <Screen2Slider key="2" onNext={next} data-id="element-252" />, <Screen3Ranking key="3" onNext={next} data-id="element-253" />, <Screen4Reveal key="4" onNext={next} data-id="element-254" />, <Screen5Memory key="5" onNext={next} data-id="element-255" />, <Screen6Video key="6" onNext={next} data-id="element-256" />, <Screen7Input key="7" onRestart={restart} data-id="element-257" />];
  return <AnimatePresence data-id="element-258">
      {open && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.3
    }} className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-lg flex items-center justify-center p-8" onClick={onClose} data-id="element-259">
          {/* Side label */}
          <motion.div initial={{
        opacity: 0,
        x: -20
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        delay: 0.2
      }} className="absolute left-12 top-1/2 -translate-y-1/2 hidden lg:flex flex-col items-start space-y-3 max-w-xs" onClick={e => e.stopPropagation()} data-id="element-260">
            <div className="flex items-center space-x-2" data-id="element-261">
              <div className="h-[1px] w-8 bg-red-500" data-id="element-262" />
              <span className="text-xs tracking-[0.3em] uppercase text-red-500 font-semibold" data-id="element-263">
                Mobile Concept
              </span>
            </div>
            <h2 className="text-3xl font-bold text-white leading-tight" data-id="element-264">
              ERA: Inside Access
            </h2>
            <p className="text-sm text-slate-400 leading-relaxed" data-id="element-265">
              A premium mobile-first interactive congress participation
              experience for non-attendees.
            </p>
            <div className="flex flex-wrap gap-1.5 pt-2" data-id="element-266">
              {['Cinematic trailer', 'Video prediction', 'Field challenge', 'Who said it?', 'Peer poll', 'Open input', 'CTA'].map(tag => <span key={tag} className="text-[10px] uppercase tracking-wider text-slate-500 border border-slate-800 rounded-full px-2 py-0.5" data-id="element-267">
                  {tag}
                </span>)}
            </div>
          </motion.div>

          {/* Top-right controls */}
          <div className="absolute top-6 right-6 z-10 flex items-center space-x-2" onClick={e => e.stopPropagation()} data-id="element-268">
            <button onClick={skipToEnd} disabled={screen === 6} className="flex items-center space-x-1.5 h-10 px-4 rounded-full bg-white/5 hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed border border-white/10 text-white text-xs font-medium tracking-wide uppercase transition-colors" aria-label="Skip to end" data-id="element-269">
              <FastForward className="w-3.5 h-3.5" data-id="element-270" />
              <span data-id="element-271">Skip</span>
            </button>
            <button onClick={onClose} className="w-10 h-10 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 flex items-center justify-center text-white transition-colors" aria-label="Close teaser" data-id="element-272">
              <X className="w-5 h-5" data-id="element-273" />
            </button>
          </div>

          {/* Top-left Info Button */}
          <div className="absolute top-6 left-6 z-10 flex items-center" onClick={e => e.stopPropagation()} data-id="element-274">
            <button onClick={() => setShowInfo(true)} className="flex items-center space-x-2 h-10 px-4 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-white text-xs font-medium tracking-wide uppercase transition-colors" aria-label="Show concept info" data-id="element-275">
              <Info className="w-4 h-4" data-id="element-276" />
              <span data-id="element-277">About Mock-up</span>
            </button>
          </div>

          {/* Side nav — Back */}
          <button onClick={e => {
        e.stopPropagation();
        prev();
      }} disabled={screen === 0} className="absolute left-1/2 -translate-x-[280px] top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full bg-white/5 hover:bg-white/10 disabled:opacity-20 disabled:cursor-not-allowed border border-white/10 flex items-center justify-center text-white transition-all hover:scale-105 backdrop-blur-md" aria-label="Previous screen" data-id="element-278">
            <ChevronLeft className="w-5 h-5" data-id="element-279" />
          </button>

          {/* Side nav — Next */}
          <button onClick={e => {
        e.stopPropagation();
        next();
      }} disabled={screen === 6} className="absolute left-1/2 translate-x-[232px] top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full bg-white/5 hover:bg-white/10 disabled:opacity-20 disabled:cursor-not-allowed border border-white/10 flex items-center justify-center text-white transition-all hover:scale-105 backdrop-blur-md" aria-label="Next screen" data-id="element-280">
            <ChevronRight className="w-5 h-5" data-id="element-281" />
          </button>

          {/* Phone frame */}
          <motion.div initial={{
        scale: 0.85,
        opacity: 0,
        y: 30
      }} animate={{
        scale: 1,
        opacity: 1,
        y: 0
      }} exit={{
        scale: 0.9,
        opacity: 0,
        y: 20
      }} transition={{
        duration: 0.4,
        ease: 'easeOut'
      }} className="relative" onClick={e => e.stopPropagation()} data-id="element-282">
            {/* Phone bezel */}
            <div className="relative rounded-[3rem] bg-slate-900 p-3 shadow-[0_30px_80px_rgba(0,0,0,0.6),0_0_60px_rgba(220,38,38,0.15)] border border-slate-800" data-id="element-283">
              {/* Notch */}
              <div className="absolute top-3 left-1/2 -translate-x-1/2 w-32 h-6 bg-slate-900 rounded-b-3xl z-30 flex items-center justify-center" data-id="element-284">
                <div className="w-16 h-1 rounded-full bg-slate-700" data-id="element-285" />
              </div>

              <div className="relative overflow-hidden rounded-[2.4rem] bg-slate-950" style={{
            width: 380,
            height: 760
          }} data-id="element-286">
                {/* Status bar */}
                <div className="absolute top-0 inset-x-0 z-20 h-8 px-6 flex items-center justify-between text-[10px] text-white/90 font-medium pointer-events-none" data-id="element-287">
                  <span data-id="element-288">9:41</span>
                  <div className="flex items-center space-x-1" data-id="element-289">
                    <span data-id="element-290">●●●</span>
                    <span data-id="element-291">5G</span>
                    <span data-id="element-292">100%</span>
                  </div>
                </div>

                {/* Progress dots */}
                <div className="absolute top-10 left-0 right-0 z-20 flex justify-center space-x-1 px-8 pointer-events-none" data-id="element-293">
                  {screens.map((_, i) => <div key={i} className={`h-0.5 flex-1 rounded-full transition-all duration-500 ${i <= screen ? 'bg-red-500' : 'bg-white/15'}`} data-id="element-294" />)}
                </div>

                {/* Info Overlay */}
                <AnimatePresence data-id="element-295">
                  {showInfo && <motion.div initial={{
                opacity: 0,
                y: 20
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: 20
              }} className="absolute inset-0 z-50 bg-slate-950/95 backdrop-blur-xl p-6 flex flex-col overflow-y-auto" onClick={e => e.stopPropagation()} data-id="element-296">
                      <div className="flex justify-between items-center mb-6 mt-8" data-id="element-297">
                        <div className="inline-flex items-center space-x-2 bg-red-500/10 text-red-400 px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-wider border border-red-500/20" data-id="element-298">
                          <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" data-id="element-299" />
                          <span data-id="element-300">Concept Mock-up</span>
                        </div>
                        <button onClick={() => setShowInfo(false)} className="w-8 h-8 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors" data-id="element-301">
                          <X className="w-4 h-4 text-white" data-id="element-302" />
                        </button>
                      </div>
                      <h2 className="text-2xl font-bold text-white mb-2" data-id="element-303">
                        ERA: Inside Access
                      </h2>
                      <p className="text-sm text-slate-300 italic mb-6" data-id="element-304">
                        "Not everyone can be at ERA. Everyone can be part of
                        it."
                      </p>

                      <div className="space-y-4 text-sm text-slate-400 mb-8" data-id="element-305">
                        <p data-id="element-306">
                          <strong className="text-white" data-id="element-307">The Concept:</strong> A
                          premium, mobile-first interactive congress activation.
                          Think of it as a "Netflix backstage trailer" meets an
                          interactive event experience.
                        </p>
                        <div data-id="element-308">
                          <strong className="text-white mb-1 block" data-id="element-309">
                            Interactive Examples:
                          </strong>
                          <ul className="list-disc pl-4 space-y-1" data-id="element-310">
                            <li data-id="element-311">Video Predictions</li>
                            <li data-id="element-312">Field Challenges</li>
                            <li data-id="element-313">Peer Polls & Input</li>
                          </ul>
                        </div>
                        <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4 mt-6" data-id="element-314">
                          <strong className="text-amber-500 block mb-1 text-xs uppercase tracking-wider" data-id="element-315">
                            Important Note
                          </strong>
                          <p className="text-xs text-amber-200/90" data-id="element-316">
                            This is a complete functional mock-up built to
                            demonstrate the interactive format. All content is
                            placeholder to illustrate the vision.
                          </p>
                        </div>
                      </div>
                    </motion.div>}
                </AnimatePresence>

                {/* Screens */}
                <AnimatePresence mode="wait" data-id="element-317">
                  <motion.div key={screen} initial={{
                opacity: 0,
                x: 30
              }} animate={{
                opacity: 1,
                x: 0
              }} exit={{
                opacity: 0,
                x: -30
              }} transition={{
                duration: 0.4,
                ease: 'easeOut'
              }} className="absolute inset-0" data-id="element-318">
                    {screens[screen]}
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>

            {/* Footer hint */}
            <div className="text-center mt-4 text-xs text-slate-500 tracking-widest uppercase" data-id="element-319">
              Screen {screen + 1} / {screens.length}
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
};